import React, { useEffect, useState, useContext } from "react";
import { Avatar, Switch, Upload, Progress, message, Button, Statistic, Row, Col, Tag } from "antd";
import Text from "antd/lib/typography/Text";
import { UserOutlined, InboxOutlined, DownloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";

import { DateFormat, getApi, saveApi } from "../../../helpers";
import CardContainer from "../../../components/Card";
import TableContainer from "../../../components/Table";
import Popup from "../../../components/Popup";
import { StoreContext } from "../../../context";
import { canWrite } from "../../../helpers/permissions";

const columns = [
  {
    title: "Image",
    dataIndex: "user_profile_image",
    key: "user_profile_image",
    align: "center",
    render: (text) => (
      <Avatar icon={<UserOutlined />} size={50} alt="" src={text} />
    ),
  },
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    sorter: true,
  },
  {
    title: "Email",
    key: "email",
    dataIndex: "email",
  },
  {
    title: "Mobile",
    dataIndex: "mobile",
    key: "mobile",
  },
  {
    title: "Source",
    dataIndex: "user_source",
    key: "user_source",
    align: "center",
    render: (text) => {
      const sourceText = text || "Unknown";
      const color = sourceText.toLowerCase() === "store" ? "blue" : "green";
      return (
        <Tag color={color}>
          {sourceText.charAt(0).toUpperCase() + sourceText.slice(1)}
        </Tag>
      );
    },
    sorter: true,
  },
  {
    title: "Joining Date",
    dataIndex: "created_at",
    key: "created_at",
    align: "center",
    render: (text) => DateFormat(text),
    sorter: true,
  },
];

export default function User() {
  const { storeId } = useContext(StoreContext);

  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(8);
  const [userList, setUserList] = useState([]);
  const [selected, setSelected] = useState({});
  const [savedPayload, setSavedPayload] = useState({});

  // Import states
  const [importPopup, setImportPopup] = useState(false);
  const [xlsFile, setXlsFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(storeId || "1");

  useEffect(() => {
    getUsersList();
    getStores();
  }, []);

  const getStores = async () => {
    const res = await getApi({}, "getStores", "dashboard/");
    if (res.data && res.data.length) setStores(res.data);
  };

  const getUsersList = async (payload = {}) => {
    setLoading(true);
    const res = await getApi(payload, "getUsersList", "mobile/");
    if (res.data) {
      setUserList(res.data);
      setTotal(res.meta?.total);
    }
    setSavedPayload(payload);
    setLoading(false);
  };

  const updateStatusHandler = async () => {
    if (selected.user_id) {
      setSelected({ ...selected, loading: true });
      let is_active = 1;
      let msg = "User Activated";
      if (selected.is_active) {
        is_active = 0;
        msg = "User Deactivated";
      }
      const res = await saveApi(
        { user_id: selected.user_id, is_active: is_active },
        msg,
        "deactivateUser",
        "mobile/"
      );
      if (res) {
        setUserList(
          userList.map((user) => {
            if (user.user_id === selected.user_id) {
              return (user = { ...user, is_active: is_active });
            }
            return user;
          })
        );
      }
      setSelected({});
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let payload = { ...savedPayload };

    if (sorter && sorter.field) {
      payload.sort = {
        field: sorter.field,
        order_by: sorter.order === 'ascend' ? 'asc' : 'desc'
      };
    }

    getUsersList(payload);
  };

  // Download Sample Excel Template
  const downloadSampleTemplate = () => {
    const sampleData = [
      { "Contact Name": "John Doe", "Mobile Number": "9876543210", "Regular/Loyal": "L" },
      { "Contact Name": "Jane Smith", "Mobile Number": "9876543211", "Regular/Loyal": "R" },
      { "Contact Name": "[blank]", "Mobile Number": "9876543212", "Regular/Loyal": "L" },
      { "Contact Name": "NA", "Mobile Number": "9876543213", "Regular/Loyal": "R" }
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
    XLSX.writeFile(workbook, "Sample_Customer_Import_Template.xlsx");
    message.success("Sample template downloaded successfully!");
  };

  // Open Import Popup
  const openImportPopup = () => {
    setSelectedStore(storeId || "1");
    setImportPopup(true);
  };

  // Upload Excel File Handler
  const uploadExcelFile = ({ file }) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];

    if (!allowedTypes.includes(file.type)) {
      message.warn("Please upload only Excel files (.xlsx or .xls)");
      return;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      message.error("File size exceeds 10MB. Please upload a smaller file.");
      return;
    }

    // Just set the file without showing progress
    setXlsFile(file);
    setImportProgress(0);
  };

  // Import Customers Handler
  const importCustomersHandler = async () => {
    if (!xlsFile) {
      message.warn("Please upload Excel file first.");
      return;
    }

    if (!selectedStore) {
      message.warn("Please select a store.");
      return;
    }

    setImporting(true);
    setImportProgress(10);

    try {
      const fmData = new FormData();
      fmData.append("file", xlsFile);
      fmData.append("key", "JWT");
      fmData.append("secret", "RAPSAP");
      fmData.append("store_id", selectedStore);

      const headers = { "content-type": "multipart/form-data" };

      // Simulate progress stages
      setTimeout(() => setImportProgress(30), 300);
      setTimeout(() => setImportProgress(50), 600);

      setImportProgress(60);

      const res = await getApi(fmData, "importCustomersFromExcel", "dashboard/", headers);

      setImportProgress(90);

      if (res && res.success) {
        setImportProgress(100);
        setImportResults(res.data);
        setImportPopup(false);
        setShowResults(true);
        message.success(res.msg || "Customer import completed successfully!");
      } else {
        message.error(res.msg || "Failed to import customers");
        setImportProgress(0);
      }
    } catch (error) {
      console.error("Error importing customers:", error);
      message.error("Failed to import customers. Please try again.");
      setImportProgress(0);
    }

    setImporting(false);
  };

  // Close Results and Refresh
  const closeResultsHandler = () => {
    setShowResults(false);
    setImportResults(null);
    setXlsFile(null);
    setImportProgress(0);
    getUsersList(); // Refresh the users list
  };

  return (
    <>
      {/* Import Excel Modal */}
      {importPopup && (
        <Popup
          visible={true}
          title="Import Customers from Excel"
          cancelHandler={() => {
            if (!importing) {
              setXlsFile(null);
              setImportProgress(0);
              setSelectedStore(storeId || "1");
              setImportPopup(false);
            }
          }}
          saveHandler={importCustomersHandler}
          saveText="Import"
          width={450}
          loading={importing}
          closable={!importing}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "5px 24px",
              flexDirection: "column",
            }}
          >
            {/* Download Sample Template Button */}
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadSampleTemplate}
              style={{
                marginBottom: "20px",
                width: "100%",
                borderColor: "#52c41a",
                color: "#52c41a"
              }}
            >
              Download Sample Template
            </Button>

            {/* Store Selection Dropdown */}
            <div style={{
              width: "100%",
              marginBottom: "20px",
              textAlign: "left"
            }}>
              <label style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                fontSize: "14px"
              }}>
                Select Store <span style={{ color: "red" }}>*</span>
              </label>
              <select
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value)}
                disabled={importing}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                  fontSize: "14px",
                  backgroundColor: "white",
                  outline: "none"
                }}
              >
                <option value="">Select a store</option>
                {stores.map((store) => (
                  <option key={store.store_id} value={store.store_id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>

            {/* File Upload */}
            <Upload.Dragger
              accept=".xlsx,.xls"
              listType="picture"
              maxCount={1}
              customRequest={uploadExcelFile}
              onRemove={() => {
                setXlsFile(null);
                setImportProgress(0);
              }}
              fileList={xlsFile ? [xlsFile] : []}
              style={{ width: "100%" }}
              disabled={importing}
            >
              <div style={{ padding: "5px 15px", marginBottom: 10 }}>
                <InboxOutlined size={55} />
                <p>
                  Click or drag <span style={{ color: "blue" }}>Excel</span> file
                  to this area to import customers
                </p>
                <p style={{ fontSize: "12px", color: "#666" }}>
                  Supported formats: .xlsx, .xls (Max size: 10MB)
                </p>
              </div>
            </Upload.Dragger>

            {importProgress > 0 && importProgress < 100 ? (
              <>
                <Progress
                  percent={importProgress}
                  style={{ width: "100%", marginTop: "16px" }}
                />
                <Text style={{ marginTop: "10px", color: "#1890ff" }}>
                  {importing ? "⏳ Importing customers, please wait..." : "Uploading file..."}
                </Text>
                {importing && (
                  <Text style={{ marginTop: "5px", color: "#ff4d4f", fontSize: "12px" }}>
                    ⚠️ Do not close until import completes to avoid errors
                  </Text>
                )}
              </>
            ) : null}
          </div>
        </Popup>
      )}

      {/* Results Display Modal */}
      {showResults && importResults && (
        <Popup
          visible={true}
          title="Import Results"
          cancelHandler={closeResultsHandler}
          saveHandler={closeResultsHandler}
          saveText="OK"
          width={600}
          cancelText={null}
        >
          <div style={{ padding: "10px" }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Total Processed"
                  value={importResults.total_processed || 0}
                  prefix={<FileExcelOutlined />}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="New Customers Created"
                  value={importResults.new_customers_created || 0}
                  prefix="👥"
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="New Loyalty Added"
                  value={importResults.new_loyalty_added || 0}
                  prefix="💎"
                  valueStyle={{ color: "#faad14" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Existing Customers"
                  value={importResults.existing_customers || 0}
                  prefix="🔄"
                  valueStyle={{ color: "#722ed1" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Loyalty Added to Existing"
                  value={importResults.existing_customers_loyalty_added || 0}
                  prefix="💎"
                  valueStyle={{ color: "#13c2c2" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Skipped Users"
                  value={importResults.skipped_users || 0}
                  prefix="⚠️"
                  valueStyle={{ color: "#fa8c16" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Invalid Mobile Numbers"
                  value={importResults.skipped_invalid_mobile || 0}
                  prefix="❌"
                  valueStyle={{ color: "#f5222d" }}
                />
              </Col>
            </Row>

            {importResults.file_url && (
              <div style={{ marginTop: "20px", textAlign: "center" }}>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  href={importResults.file_url}
                  target="_blank"
                >
                  Download Uploaded File
                </Button>
              </div>
            )}
          </div>
        </Popup>
      )}

      {/* User Status Update Modal */}
      {selected.user_id && (
        <Popup
          saveText={selected.is_active ? "Yes, Deactivate" : "Yes, Activate"}
          title="Update User Active Status"
          saveHandler={updateStatusHandler}
          visible={true}
          cancelHandler={() => setSelected({})}
          loading={selected.loading}
        >
          <Text strong style={{ fontSize: 18, textTransform: "capitalize" }}>
            Are you sure to {selected.is_active ? "Deactivate" : "Activate"} the
            user {selected.name}?
          </Text>
        </Popup>
      )}

      <CardContainer hoverable={false}>
        <TableContainer
          breadcrumbTitle="User Management"
          loading={loading}
          columns={[
            ...columns,
            {
              title: "Action",
              dataIndex: "is_active",
              key: "is_active",
              align: "center",
              render: (text, object) => (
                <Switch
                  size="small"
                  checked={text ? true : false}
                  onChange={(c) => setSelected(object)}
                />
              ),
            },
          ]}
          data={userList}
          total={total}
          searchFor="Name, Mobile No."
          functionsHandler={getUsersList}
          rowClickHandler={(record) => {}}
          parseButton={canWrite() ? "Import" : null}
          parseHandler={openImportPopup}
          onChange={handleTableChange}
        />
      </CardContainer>
    </>
  );
}